import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import { formatTimestamp, formatToDayMonthYear, formatCurrency } from "../../utils/TextUtils.js";
import { allowedToView } from "../../utils/PermissionUtils.js";

function ExpenseView() {
    const [expense, setExpense] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deletionReason, setDeletionReason] = useState("");
    const navigate = useNavigate();
    const { id } = useParams();
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");

    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin', 'collection_center_manager']));
    };

    // Fetch expense details
    const fetchExpense = async () => {
        try {
            setLoading(true);
            setError("");
            
            const response = await fetch(`${API_URL}/expenses/${id}`, {
                headers: { "Authorization": `Bearer ${token}` },
            });

            if (response.ok) {
                const data = await response.json();
                setExpense(data);
            } else if (response.status === 401) {
                navigate("/logout");
            } else if (response.status === 404) {
                setError("Expense not found.");
                setNotification({ type: "danger", message: "Expense not found." });
            } else {
                throw new Error("Failed to fetch expense details");
            }
        } catch (error) {
            setError("An error occurred while fetching expense details.");
            setNotification({ type: "danger", message: "Failed to load expense details." });
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExpense();
    }, [id]);

    const handleDelete = async () => {
        if (!deletionReason.trim()) {
            setNotification({ type: "danger", message: "Please provide a reason for deletion." });
            return;
        }

        try {
            const response = await fetch(`${API_URL}/expenses/${id}`, {
                method: 'DELETE',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ deletion_reason: deletionReason }),
            });

            if (response.ok) {
                setNotification({ type: "success", message: 'Expense deleted successfully' });
                setIsDeleteDialogOpen(false);
                // Refresh the data
                fetchExpense();
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                const errorData = await response.json();
                setNotification({ 
                    type: "danger", 
                    message: errorData.message || 'There was an error deleting the expense.' 
                });
            }
        } catch (error) {
            setNotification({ 
                message: 'An error occurred while deleting the expense.', 
                type: 'danger' 
            });
            console.error("Error:", error);
        }
    };

    const handleRestore = async () => {
        try {
            const response = await fetch(`${API_URL}/expenses/${id}/restore`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                setNotification({ type: "success", message: 'Expense restored successfully' });
                setIsRestoreDialogOpen(false);
                fetchExpense();
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                const errorData = await response.json();
                setNotification({ 
                    type: "danger", 
                    message: errorData.message || 'There was an error restoring the expense.' 
                });
            }
        } catch (error) {
            setNotification({ 
                message: 'An error occurred while restoring the expense.', 
                type: 'danger' 
            });
            console.error("Error:", error);
        }
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    // Helper function to get user display name
    const getUserDisplayName = (user) => {
        if (!user) return 'N/A';
        const firstName = user.first_name || '';
        const lastName = user.last_name || '';
        const username = user.username || '';
        
        if (firstName && lastName) {
            return `${firstName} ${lastName}`;
        } else if (firstName) {
            return firstName;
        } else if (username) {
            return username;
        }
        return 'N/A';
    };

    // Helper function to render user with link
    const renderUserWithLink = (user, actionDate = null) => {
        if (!user) return <span className="has-text-grey-light">N/A</span>;
        
        const userId = user.id;
        const displayName = getUserDisplayName(user);
        
        return (
            <div className="user-info">
                <Link 
                    to={`/dashboard/users/${userId}/details`}
                    className="no_deco_link has-text-weight-medium"
                    style={{ textDecoration: 'none' }}
                >
                    <span className="icon is-small">
                        <i className="fas fa-user-circle"></i>
                    </span>
                    &nbsp; 
                    {displayName}
                </Link>
                {actionDate && (
                    <span className="has-text-grey-light" style={{ fontSize: '0.9rem', display: 'block', marginTop: '0.25rem' }}>
                        {formatTimestamp(actionDate)}
                    </span>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="has-text-centered" style={{ padding: "3rem" }}>
                <i className="fa fa-spinner fa-spin fa-2x"></i>
                <p>Loading expense details...</p>
            </div>
        );
    }

    if (error && !expense) {
        return (
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <p className="title">Expense Details</p>
                        </div>
                    </div>
                    <div className="level-right">
                        <p className="level-item">
                            <Link to="/dashboard/expenses" className="button primary-bg">
                                <i className="fa fa-arrow-left"></i> &nbsp; Back
                            </Link>
                        </p>
                    </div>
                </nav>
                <div className="notification is-danger">
                    <button className="delete" onClick={() => setError("")}></button>
                    {error}
                </div>
            </main>
        );
    }

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">Expense Details</p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/expenses" className="button primary-bg">
                            <i className="fa fa-arrow-left"></i> &nbsp; Back
                        </Link>
                    </p>
                </div>
            </nav>

            {notification.message && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    onClose={closeNotification}
                />
            )}

            {expense && (
                <>
                    {/* Status Banner */}
                    {expense.is_deleted && (
                        <div className="notification is-danger">
                            <strong>Deleted:</strong> This expense was deleted on {formatTimestamp(expense.deleted_at)}
                            {expense.deleted_by && <span> by {getUserDisplayName(expense.deleted_by)}</span>}
                            {expense.deletion_reason && (
                                <span>
                                    <br />
                                    <strong>Reason:</strong> {expense.deletion_reason}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="columns">
                        <div className="column is-8">
                            {/* Main Details Card */}
                            <div className="card">
                                <header className="card-header">
                                    <p className="card-header-title">
                                        <span className="icon">
                                            <i className="fas fa-receipt"></i>
                                        </span>
                                        <span>Expense Information</span>
                                    </p>
                                    <div className="card-header-icon">
                                        {expense.is_deleted ? (
                                            <span className="tag is-danger is-medium">Deleted</span>
                                        ) : (
                                            <span className="tag is-success is-medium">Active</span>
                                        )}
                                    </div>
                                </header>
                                <div className="card-content">
                                    <div className="columns is-multiline">
                                        <div className="column is-12">
                                            <div className="field">
                                                <label className="label has-text-grey">Expense Name</label>
                                                <p className="subtitle is-5">{expense.name}</p>
                                            </div>
                                        </div>

                                        <div className="column is-6">
                                            <div className="field">
                                                <label className="label has-text-grey">Amount</label>
                                                <p className="subtitle is-4">
                                                    {formatCurrency(expense.amount)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="column is-6">
                                            <div className="field">
                                                <label className="label has-text-grey">Incurred On</label>
                                                <p className="subtitle is-5">
                                                    {formatToDayMonthYear(expense.incurred_on)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="column is-12">
                                            <div className="field">
                                                <label className="label has-text-grey">Description</label>
                                                <div className="box" style={{ backgroundColor: '#f5f5f5' }}>
                                                    {expense.description || 'No description provided.'}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="column is-6">
                                            <div className="field">
                                                <label className="label has-text-grey">Type</label>
                                                <p>
                                                    {expense.is_route_specific ? (
                                                        <span className="tag is-info is-medium">Route Specific</span>
                                                    ) : (
                                                        <span className="tag is-light is-medium">General</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="column is-6">
                                            <div className="field">
                                                <label className="label has-text-grey">Route</label>
                                                <p>
                                                    {expense.is_route_specific ? (
                                                        expense.collection_route?.name || 'N/A'
                                                    ) : (
                                                        <span className="has-text-grey-light">All Routes</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Created By Information */}
                            <div className="card" style={{ marginTop: "1rem" }}>
                                <header className="card-header">
                                    <p className="card-header-title">
                                        <span className="icon has-text-primary">
                                            <i className="fas fa-user-plus"></i>
                                        </span>
                                        <span>Created By</span>
                                    </p>
                                </header>
                                <div className="card-content">
                                    <div className="columns is-multiline">
                                        <div className="column is-12">
                                            {renderUserWithLink(expense.created_by, expense.created_at)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Updated By Information */}
                            {expense.updated_by && (
                                <div className="card" style={{ marginTop: "1rem" }}>
                                    <header className="card-header">
                                        <p className="card-header-title">
                                            <span className="icon has-text-warning">
                                                <i className="fas fa-user-edit"></i>
                                            </span>
                                            <span>Last Updated By</span>
                                        </p>
                                    </header>
                                    <div className="card-content">
                                        <div className="columns is-multiline">
                                            <div className="column is-12">
                                                {renderUserWithLink(expense.updated_by, expense.updated_at)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Deleted By Information (if deleted) */}
                            {expense.is_deleted && expense.deleted_by && (
                                <div className="card" style={{ marginTop: "1rem" }}>
                                    <header className="card-header">
                                        <p className="card-header-title">
                                            <span className="icon has-text-danger">
                                                <i className="fas fa-user-times"></i>
                                            </span>
                                            <span>Deleted By</span>
                                        </p>
                                    </header>
                                    <div className="card-content">
                                        <div className="columns is-multiline">
                                            <div className="column is-12">
                                                {renderUserWithLink(expense.deleted_by, expense.deleted_at)}
                                                {expense.deletion_reason && (
                                                    <div className="box" style={{ backgroundColor: '#fef2f2', marginTop: '0.5rem' }}>
                                                        <strong>Reason:</strong> {expense.deletion_reason}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar - Actions */}
                        <div className="column is-4">
                            <div className="card">
                                <header className="card-header">
                                    <p className="card-header-title">
                                        <span className="icon">
                                            <i className="fas fa-cog"></i>
                                        </span>
                                        <span>Actions</span>
                                    </p>
                                </header>
                                <div className="card-content">
                                    {canEdit(userRole) && (
                                        <div className="buttons are-medium is-fullwidth" style={{ flexDirection: 'column' }}>
                                            {!expense.is_deleted ? (
                                                <>
                                                    <Link 
                                                        to={`/dashboard/expenses/${id}/edit`} 
                                                        className="button is-fullwidth"
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-edit"></i>
                                                        </span>
                                                        <span>Edit Expense</span>
                                                    </Link>
                                                    <button 
                                                        className="button is-danger is-fullwidth"
                                                        onClick={() => setIsDeleteDialogOpen(true)}
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-trash-alt"></i>
                                                        </span>
                                                        <span>Delete Expense</span>
                                                    </button>
                                                </>
                                            ) : (
                                                <button 
                                                    className="button is-success is-fullwidth"
                                                    onClick={() => setIsRestoreDialogOpen(true)}
                                                >
                                                    <span className="icon">
                                                        <i className="fas fa-undo"></i>
                                                    </span>
                                                    <span>Restore Expense</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {!canEdit(userRole) && (
                                        <div className="buttons are-medium is-fullwidth" style={{ flexDirection: 'column' }}>
                                            <button className="button is-light is-fullwidth" disabled>
                                                <span className="icon">
                                                    <i className="fas fa-lock"></i>
                                                </span>
                                                <span>View Only</span>
                                            </button>
                                            <p className="help has-text-centered">
                                                You do not have permission to modify this expense.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Delete Confirmation Dialog */}
                    {isDeleteDialogOpen && (
                        <div className="modal is-active">
                            <div className="modal-background" onClick={() => setIsDeleteDialogOpen(false)}></div>
                            <div className="modal-card">
                                <header className="modal-card-head">
                                    <p className="modal-card-title">Confirm Delete</p>
                                    <button className="delete" aria-label="close" onClick={() => setIsDeleteDialogOpen(false)}></button>
                                </header>
                                <section className="modal-card-body">
                                    <p>Are you sure you want to delete this expense?</p>
                                    <div className="box" style={{ marginTop: "1rem" }}>
                                        <p><strong>Name:</strong> {expense?.name}</p>
                                        <p><strong>Amount:</strong> {formatCurrency(expense?.amount)}</p>
                                        <p><strong>Date:</strong> {formatToDayMonthYear(expense?.incurred_on)}</p>
                                        {expense?.is_route_specific && expense?.collection_route?.name && (
                                            <p><strong>Route:</strong> {expense.collection_route.name}</p>
                                        )}
                                    </div>
                                    
                                    <div className="field">
                                        <label className="label">Reason for Deletion <span className="has-text-danger">*</span></label>
                                        <div className="control">
                                            <textarea
                                                className="textarea"
                                                placeholder="Please provide a reason for deleting this expense..."
                                                value={deletionReason}
                                                onChange={(e) => setDeletionReason(e.target.value)}
                                                rows="3"
                                            />
                                        </div>
                                        <p className="help">This reason will be recorded for audit purposes.</p>
                                    </div>
                                    
                                    <p className="has-text-danger">This action can be reversed by restoring the expense.</p>
                                </section>
                                <footer className="modal-card-foot">
                                    <button 
                                        className="button is-danger" 
                                        onClick={handleDelete}
                                        disabled={!deletionReason.trim()}
                                    >
                                        <i className="fa fa-trash"></i>&nbsp; Delete
                                    </button>
                                    <button className="button" onClick={() => setIsDeleteDialogOpen(false)}>
                                        Cancel
                                    </button>
                                </footer>
                            </div>
                        </div>
                    )}

                    {/* Restore Confirmation Dialog */}
                    {isRestoreDialogOpen && (
                        <div className="modal is-active">
                            <div className="modal-background" onClick={() => setIsRestoreDialogOpen(false)}></div>
                            <div className="modal-card">
                                <header className="modal-card-head">
                                    <p className="modal-card-title">Confirm Restore</p>
                                    <button className="delete" aria-label="close" onClick={() => setIsRestoreDialogOpen(false)}></button>
                                </header>
                                <section className="modal-card-body">
                                    <p>Are you sure you want to restore this expense?</p>
                                    <div className="box" style={{ marginTop: "1rem" }}>
                                        <p><strong>Name:</strong> {expense?.name}</p>
                                        <p><strong>Amount:</strong> {formatCurrency(expense?.amount)}</p>
                                        <p><strong>Date:</strong> {formatToDayMonthYear(expense?.incurred_on)}</p>
                                        {expense?.deleted_by && (
                                            <p><strong>Deleted By:</strong> {getUserDisplayName(expense.deleted_by)}</p>
                                        )}
                                        {expense?.deleted_at && (
                                            <p><strong>Deleted At:</strong> {formatTimestamp(expense.deleted_at)}</p>
                                        )}
                                        {expense?.deletion_reason && (
                                            <p><strong>Deletion Reason:</strong> {expense.deletion_reason}</p>
                                        )}
                                    </div>
                                    <p className="has-text-success">This will make the expense visible and editable again.</p>
                                </section>
                                <footer className="modal-card-foot">
                                    <button className="button is-success" onClick={handleRestore}>
                                        <i className="fa fa-undo"></i>&nbsp; Restore
                                    </button>
                                    <button className="button" onClick={() => setIsRestoreDialogOpen(false)}>
                                        Cancel
                                    </button>
                                </footer>
                            </div>
                        </div>
                    )}
                </>
            )}
        </main>
    );
}

export default ExpenseView;